import * as Yup from "yup";
import { useFormik } from "formik";
import classNames from "classnames";

import AscModal from "../AscModal/AscModal";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscButton from "../AscButton/AscButton";
import AscRichTextEditor from "../AscRichTextEditor/AscRichTextEditor";
import AscTextInput from "../AscInputFields/AscTextInput/AscTextInput";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import { BatchStudentType, BatchSendEmailFormValues } from "@ascendnow/shared";
import styles from "./AscBatchSendEmail.module.scss";
import AscCheckBox from "../AscInputFields/AscCheckBox/AscCheckBox";
import { ChangeEvent, useEffect, useMemo } from "react";

export interface AscBatchSendEmailProps {
  className?: string;
  visible: boolean;
  setVisible: (val: boolean) => void;
  sendMessage: (values: BatchSendEmailFormValues) => void;
  studentOptions: Pick<BatchStudentType, "id" | "email" | "name">[];
  selectedStudentId: string;
}

export default function AscBatchSendEmail({
  className,
  visible,
  setVisible,
  sendMessage,
  studentOptions,
  selectedStudentId,
}: AscBatchSendEmailProps) {
  const selectedStudentEmail = useMemo(() => {
    const email = studentOptions.find(
      (el) => el.id === selectedStudentId
    )?.email;
    return email ? [email] : [];
  }, [studentOptions, selectedStudentId]);
  const formik = useFormik({
    initialValues: {
      email: selectedStudentEmail,
      isAll: false,
      subject: "",
      message: "",
    },
    validationSchema: Yup.object({
      email: Yup.array()
        .of(Yup.string().email("Email is invalid"))
        .min(1, "At least one student should be selected")
        .required("Please select a receiver"),
      subject: Yup.string().required("Please enter a topic"),
      message: Yup.string().required("Please enter a message"),
    }),
    onSubmit: sendMessage,
  });

  useEffect(() => {
    formik.setFieldValue("email", selectedStudentEmail);
  }, [selectedStudentEmail]);
  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      withBackground={false}
      modalClassName={styles.modal}
    >
      <div className={styles.container} data-testid="asc-batch-send-email">
        <AscHeading level={6} className={styles.title}>
          Create email
        </AscHeading>

        <form className={styles.form}>
          <div>
            <div className={styles.formGroup}>
              <AscSelectInput
                className={classNames(styles.input, styles.positionInput)}
                innerInputClassName={styles.selectInnerInput}
                fullWidth={true}
                id="email"
                name="email"
                placeholder="Select a receiver"
                onBlur={formik.handleBlur}
                value={formik?.values?.email}
                multiple
                disabled={formik.values.isAll}
                onChange={(emails, selections) =>
                  formik.setFieldValue("email", emails)
                }
                optionContainerClassName={classNames(styles.optionStyle)}
                selectedOptionClassName={styles.selectedOptionClassName}
              >
                {studentOptions?.map((receiver) => (
                  <AscSelectInput.Option
                    key={receiver.id}
                    value={receiver.email}
                    label={receiver.name}
                  />
                ))}
              </AscSelectInput>

              {/* <AscTextInput
              className={classNames(styles.input, {
                [styles.errorInput]:
                  formik.errors.email && formik.touched.email,
              })}
              fullWidth={true}
              id="email"
              name="email"
              placeholder="kellyclarson@gmail.com"
              value={formik.values.email}
              readOnly
            /> */}
            </div>
            <div className={styles.formGroupCheckbox}>
              <AscCheckBox
                name="isAll"
                checked={formik.values.isAll}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  formik.handleChange(event);
                  if (event.target.checked) {
                    formik.setFieldValue(
                      "email",
                      studentOptions.map((el) => el.email)
                    );
                  } else {
                    formik.setFieldValue("email", selectedStudentEmail);
                  }
                  formik.setFieldValue("isAll", event.target.checked);
                }}
                onBlur={formik.handleBlur}
              />
              <label>Send an email to all students</label>
            </div>
          </div>

          <div className={classNames(styles.formGroup, styles.emailTopic)}>
            <AscTextInput
              className={classNames(styles.input, {
                [styles.errorInput]:
                  formik.errors.subject && formik.touched.subject,
              })}
              fullWidth={true}
              id="subject"
              name="subject"
              placeholder="Title/Topic"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.subject}
            />
            {formik.errors.subject && formik.touched.subject && (
              <div className={styles.fieldError}>{formik.errors.subject}</div>
            )}
          </div>

          <div className={styles.formGroup}>
            <AscRichTextEditor
              editable={true}
              initialInferValue={formik.initialValues.message}
              onHtmlChange={(val) => formik.setFieldValue("message", val)}
              inputWrapperClassname={styles.richTextEditorInput}
            />
          </div>
        </form>

        <AscButton
          className={classNames(styles.sendBtn)}
          type="submit"
          onClick={() => formik?.handleSubmit()}
        >
          Send
        </AscButton>
      </div>
    </AscModal>
  );
}
